import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import isEmpty from 'lodash/isEmpty'
import { Optional } from '@rio/types'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { actions, selectors, UserData } from 'builder/modules/user'
import { useEffectOnMount } from 'builder/hooks/useEffectOnMount'

type Props = {
  firstNameFromResume: Optional<string>
  lastNameFromResume: Optional<string>
}

const DEBOUNCE_TIME = 10000

export const useSetAccountDataIfEmpty = ({ firstNameFromResume, lastNameFromResume }: Props) => {
  const { firstName, lastName, email } = useTypedSelector(selectors.userData) || {}
  const dispatch = useDispatch()
  const dataToSet = useRef<Partial<UserData>>({})

  useEffect(() => {
    if (firstName && lastName) {
      return
    }

    if (!firstName && firstNameFromResume) {
      dataToSet.current.firstName = firstNameFromResume
    }

    if (!lastName && lastNameFromResume) {
      dataToSet.current.lastName = lastNameFromResume
    }
  }, [firstNameFromResume, lastNameFromResume, firstName, lastName])

  // set account data on unmount
  useEffectOnMount(() => {
    return () => {
      if (isEmpty(dataToSet.current)) {
        return
      }
      dispatch(actions.updateUserRequest({ email, firstName, lastName, ...dataToSet.current }))
      dataToSet.current = {}
    }
  })

  // set account data on debounce
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isEmpty(dataToSet.current)) {
        return
      }
      dispatch(actions.updateUserRequest({ email, firstName, lastName, ...dataToSet.current }))
      dataToSet.current = {}
    }, DEBOUNCE_TIME)

    return () => {
      clearTimeout(timeout)
    }
  }, [firstNameFromResume, lastNameFromResume, dispatch, email, firstName, lastName])
}
